import { Country, IBuilding, IGeographyPointLocation, IMealPeriod, IRegion } from "@dining/api-client";
import { Dropdown, IDropdownOption, IStackTokens, Label, Link, Stack, Text } from "@fluentui/react";
import { createSelector } from "@reduxjs/toolkit";
import MDEditor from "@uiw/react-md-editor";
import React, { useEffect, useState } from "react";
import { Controller, DeepMap, FieldError, useFieldArray, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { validTimes } from "../../common/commonConstants";
import { handleInputCheck } from "../../common/invalidCharCheck";
import { fetchBuildings, setUpdatedCafeCountries } from "../../reducers/diningReducer";

import {
  buildingListSelector,
  cafeSelector,
  countriesSelector,
  editedCafeSelector,
  regionListSelector,
  updateCafeModeSelector,
} from "../../selectors/diningSelector";
import styles from "../../styles/updateCafe.module.css";
import { linkCommand } from "./MDEditorCommands";

export type UpdateCafeFormFields = {
  cafeRegion: string;
  cafeCountry: string;
  cafeLocation: string;
  cafeName: string;
  mealPeriods: IMealPeriod[];
  currencyName: string;
  currencySymbol: string;
  cafeDisclaimer: string;
  location: IGeographyPointLocation;
};

interface RegionDropdownOption extends IDropdownOption {
  countries?: Country[];
}

// Option selector construction, in order of apperance
const regionOptionsSelector = createSelector(regionListSelector, (regionList) =>
  regionList.map(
    (region: IRegion) =>
      ({ key: region.id, text: region.name, value: region.id, countries: region.countries } as RegionDropdownOption),
  ),
);

const countriesOptionsSelector = createSelector(countriesSelector, (countries) =>
  countries.map((country) => ({ key: country.code ?? "", text: country.name, value: country.code } as IDropdownOption)),
);

export interface IBuildingDropDownOption extends IDropdownOption {
  key: string;
  text: string;
  value: string;
  location: IGeographyPointLocation;
}

const buildingOptionsSelector = createSelector(buildingListSelector, (buildingList) =>
  buildingList.map(
    (building: IBuilding) =>
      ({
        key: building.id,
        text: Number.isNaN(parseInt(building.name, 10)) ? building.name : `Building ${building.name}`,
        value: building.id,
        location: building.location,
      } as IBuildingDropDownOption),
  ),
);

export const UpdateCafeForm = () => {
  const buildingOptions = useSelector(buildingOptionsSelector);
  const regions = useSelector(regionListSelector);
  const regionOptions = useSelector(regionOptionsSelector);
  const countriesOptions = useSelector(countriesOptionsSelector);
  const selectedCafe = useSelector(cafeSelector);
  const timeOptions = validTimes.map((time: string) => ({ key: time, text: time, value: time } as IDropdownOption));
  const updateCafeMode = useSelector(updateCafeModeSelector);
  const resetCafe = useSelector(editedCafeSelector);
  const methods = useFormContext<UpdateCafeFormFields>();
  const [isCustomLocation, setIsCustomLocation] = useState(false);
  const [invalidCharError, setInvalidCharError] = useState("");

  const gapToken: IStackTokens = { childrenGap: 5 };

  const dispatch = useDispatch();

  //Git Source: https://github.com/uiwjs/react-md-editor/blob/master/core/src/commands/link.tsx
  /**
   *  check if selected building's location equals to the lat/lon and set state of custom lot/lon checkbox
   */
  const checkIfLocationisCustomized = () => {
    const selectedCafeBuilding = buildingOptions.find((item) => item.key === selectedCafe?.buildingId);
    const isCustomized = !(
      selectedCafeBuilding?.location?.coordinates[0] === selectedCafe?.location?.coordinates[0] &&
      selectedCafeBuilding?.location?.coordinates[1] === selectedCafe?.location?.coordinates[1]
    );
    setIsCustomLocation(isCustomized);
  };

  useEffect(() => {
    checkIfLocationisCustomized();
  }, [buildingOptions]);

  useEffect(() => {
    if (selectedCafe != null && selectedCafe.regionId) {
      const region = regions.find((r) => r.id === selectedCafe.regionId);
      dispatch(setUpdatedCafeCountries(region?.countries ?? []));
    }
    dispatch(fetchBuildings(selectedCafe?.countryCode));
  }, [selectedCafe]);

  useEffect(() => {
    setTimeout(() => methods.reset(resetCafe), 100);
  }, [methods.reset, resetCafe]);

  const { fields, append, remove } = useFieldArray<IMealPeriod>({
    control: methods.control,
    name: "mealPeriods",
  });

  const locationErrorTextDisplay = (
    location: DeepMap<IGeographyPointLocation, FieldError> | undefined,
    index: number,
  ): string | undefined => {
    return location?.coordinates ? location?.coordinates[index]?.message : "";
  };

  return (
    <Stack tokens={gapToken} verticalFill={false} verticalAlign={"start"}>
      <Controller
        control={methods.control}
        name={"cafeRegion"}
        defaultValue={selectedCafe?.regionId}
        rules={{
          required: { value: true, message: "Required field." },
        }}
        render={({ onChange, onBlur, value }) => (
          <Dropdown
            onBlur={onBlur}
            label={"Region"}
            options={regionOptions}
            aria-label={"Choose a region"}
            selectedKey={value}
            onChange={(e, option) => {
              onChange(option?.key.toString());
              dispatch(setUpdatedCafeCountries((option as RegionDropdownOption)?.countries ?? []));
            }}
            errorMessage={methods.errors?.cafeRegion?.message ? methods.errors?.cafeRegion?.message : undefined}
            required
          />
        )}
      />
      <Controller
        control={methods.control}
        name={"cafeCountry"}
        defaultValue={selectedCafe?.countryCode}
        rules={{
          required: { value: true, message: "Required field." },
        }}
        render={({ onChange, onBlur, value }) => (
          <Dropdown
            onBlur={onBlur}
            label={"Country"}
            options={countriesOptions}
            aria-label={"Choose a country"}
            selectedKey={value}
            onChange={(e, option) => {
              onChange(option?.key.toString());
              if (option) dispatch(fetchBuildings(option.key.toString()));
            }}
            disabled={countriesOptions.length == 0}
            errorMessage={methods.errors?.cafeCountry?.message ? methods.errors?.cafeCountry?.message : undefined}
            required
          />
        )}
      />
      <Controller
        control={methods.control}
        name={"cafeLocation"}
        defaultValue={selectedCafe?.location}
        rules={{
          required: { value: true, message: "Required field." },
        }}
        render={({ onChange, onBlur, value }) => (
          <Dropdown
            onBlur={onBlur}
            onChange={(e, option) => onChange(option?.key.toString())}
            label={"Cafe location (i.e. building or campus)"}
            options={buildingOptions}
            aria-label={"Choose a location, such as a building or campus"}
            selectedKey={value}
            errorMessage={methods.errors?.cafeLocation?.message ? methods.errors?.cafeLocation?.message : undefined}
            required
          />
        )}
      />
      <Stack.Item>
        <Label>
          Cafe name <span className={styles.required}>*</span>
        </Label>
        <input
          type={"text"}
          className={methods.errors?.cafeName?.message ? styles.textInputBoxError : styles.textInputBox}
          name={"cafeName"}
          placeholder={"Cafe name"}
          aria-label={"Cafe name, required"}
          aria-invalid={methods.errors.cafeName ? "true" : "false"}
          data-testid={"cafeName"}
          onChange={(e) => setInvalidCharError(handleInputCheck(e.target.value))}
          ref={methods.register({
            required: { value: true, message: "Required field." },
          })}
        />
        {invalidCharError && <p className={styles.errorMessage}>{invalidCharError}</p>}
        {methods.errors?.cafeName?.message && (
          <Text role={"alert"} className={styles.errorMessage}>
            {methods.errors?.cafeName?.message}
          </Text>
        )}
      </Stack.Item>
      <Stack.Item>
        <Label>
          <input
            type="checkbox"
            checked={isCustomLocation}
            onChange={(e) => {
              setIsCustomLocation(e.target.checked);
            }}
          ></input>
          Use custom location
        </Label>
      </Stack.Item>
      {isCustomLocation && (
        <Stack horizontal={true} tokens={gapToken} horizontalAlign={"space-between"}>
          <Stack.Item>
            <Label>
              Latitude<span className={styles.required}>*</span>
            </Label>
            <input
              type={"text"}
              className={methods.errors?.location ? styles.textInputBoxError : styles.textInputBox}
              name={"location.coordinates[0]"}
              placeholder={"Latitude"}
              aria-label={"latitude number, required"}
              aria-invalid={methods.errors.location?.latitude?.message ? "true" : "false"}
              data-testid={"latitude"}
              ref={methods.register({
                required: { value: true, message: "Required field." },
                validate: {
                  validValue: (value) => !isNaN(value) || "Invalid value.",
                },
              })}
            />
            <Text role={"alert"} className={styles.errorMessage}>
              {locationErrorTextDisplay(methods.errors?.location, 0)}
            </Text>
          </Stack.Item>
          <Stack.Item>
            <Label>
              Longitude<span className={styles.required}>*</span>
            </Label>
            <input
              type={"text"}
              className={methods.errors?.location ? styles.textInputBoxError : styles.textInputBox}
              name={"location.coordinates[1]"}
              placeholder={"Longitude"}
              aria-label={"longitude number, required"}
              aria-invalid={methods.errors.location?.longitude?.message ? "true" : "false"}
              data-testid={"longitude"}
              ref={methods.register({
                required: { value: true, message: "Required field." },
                validate: {
                  validValue: (value) => !isNaN(value) || "Invalid value.",
                },
              })}
            />
            <Text role={"alert"} className={styles.errorMessage}>
              {locationErrorTextDisplay(methods.errors?.location, 1)}
            </Text>
          </Stack.Item>
        </Stack>
      )}
      <Stack horizontal={true} tokens={gapToken} horizontalAlign={"space-between"}>
        <Stack.Item>
          <Label>
            Currency Name <span className={styles.required}>*</span>
          </Label>
          <input
            type={"text"}
            className={methods.errors?.currencyName?.message ? styles.textInputBoxError : styles.textInputBox}
            name={"currencyName"}
            defaultValue={updateCafeMode === "Edit" ? selectedCafe?.currencyName : undefined}
            placeholder={"USD or EUR"}
            aria-label={"cafe currency name, required"}
            aria-invalid={methods.errors.currencyName ? "true" : "false"}
            data-testid={"currencyName"}
            ref={methods.register({
              required: { value: true, message: "Required field." },
            })}
          />
          {methods.errors?.currencyName?.message && (
            <Text role={"alert"} className={styles.errorMessage}>
              {methods.errors?.currencyName?.message}
            </Text>
          )}
        </Stack.Item>
        <Stack.Item>
          <Label>
            Currency Symbol <span className={styles.required}>*</span>{" "}
          </Label>
          <input
            type={"text"}
            className={methods.errors?.currencySymbol?.message ? styles.textInputBoxError : styles.textInputBox}
            name={"currencySymbol"}
            defaultValue={updateCafeMode === "Edit" ? selectedCafe?.currencySymbol : undefined}
            placeholder={"$ or €"}
            aria-label={"cafe currency symbol, required"}
            aria-invalid={methods.errors.currencySymbol ? "true" : "false"}
            data-testid={"currencySymbol"}
            ref={methods.register({
              required: { value: true, message: "Required field." },
            })}
          />
          {methods.errors?.currencySymbol?.message && (
            <Text role={"alert"} className={styles.errorMessage}>
              {methods.errors?.currencySymbol?.message}
            </Text>
          )}
        </Stack.Item>
      </Stack>
      {fields.map((item, index) => {
        const fieldName = `mealPeriods[${index}]`;
        return (
          <Stack key={item.id} horizontal={true} tokens={gapToken} horizontalAlign={"space-between"}>
            <fieldset name={fieldName} className={styles.mealPeriod}>
              <Stack>
                <Stack.Item>
                  <Label>
                    Meal period name <span className={styles.required}>*</span>
                  </Label>
                  <input
                    type={"text"}
                    defaultValue={item.name}
                    className={
                      methods.errors?.mealPeriods && methods.errors?.mealPeriods[index]?.name?.message != null
                        ? styles.textInputBoxError
                        : styles.textInputBox
                    }
                    name={`${fieldName}.name`}
                    placeholder={"Name of the period"}
                    aria-label={"Meal period name, required"}
                    aria-invalid={
                      methods.errors?.mealPeriods && methods.errors?.mealPeriods[index]?.name?.message != null
                        ? "true"
                        : "false"
                    }
                    data-testid={`${fieldName}.name`}
                    ref={methods.register({
                      required: { value: true, message: "Required field." },
                    })}
                  />
                  {methods.errors?.mealPeriods && methods.errors?.mealPeriods[index]?.name?.message != null && (
                    <Text role={"alert"} className={styles.errorMessage}>
                      {methods.errors?.mealPeriods[index]?.name?.message}
                    </Text>
                  )}
                </Stack.Item>
                <Controller
                  control={methods.control}
                  name={`${fieldName}.startsOn`}
                  defaultValue={item.startsOn}
                  rules={{
                    required: { value: true, message: "Required field." },
                  }}
                  render={({ onChange, onBlur, value }) => (
                    <Dropdown
                      onBlur={onBlur}
                      onChange={(e, option) => onChange(option?.key.toString())}
                      placeholder={"--:--"}
                      dropdownWidth={75}
                      aria-label={"Cafe opens"}
                      label={"Cafe opens"}
                      options={timeOptions}
                      selectedKey={value}
                      errorMessage={
                        methods.errors?.mealPeriods && methods.errors?.mealPeriods[index]?.startsOn?.message != null
                          ? methods.errors?.mealPeriods[index]?.startsOn?.message
                          : undefined
                      }
                      required
                    />
                  )}
                />
              </Stack>
              <Stack>
                <Controller
                  control={methods.control}
                  name={`${fieldName}.endsOn`}
                  defaultValue={item.endsOn}
                  rules={{
                    required: { value: true, message: "Required field." },
                  }}
                  render={({ onChange, onBlur, value }) => (
                    <Dropdown
                      onBlur={onBlur}
                      onChange={(e, option) => onChange(option?.key.toString())}
                      placeholder={"--:--"}
                      dropdownWidth={75}
                      aria-label={"Cafe closes"}
                      label={"Cafe closes"}
                      options={timeOptions}
                      selectedKey={value}
                      errorMessage={
                        methods.errors?.mealPeriods && methods.errors?.mealPeriods[index]?.endsOn?.message != null
                          ? methods.errors?.mealPeriods[index]?.endsOn?.message
                          : undefined
                      }
                      required
                    />
                  )}
                />
              </Stack>
              <Stack>
                <Link onClick={() => remove(index)}>Remove</Link>
              </Stack>
            </fieldset>
          </Stack>
        );
      })}
      <Link onClick={() => append({})}>Add meal period</Link>
      <Stack.Item>
        <Label>
          Disclaimer <span className={styles.required}>*</span>
        </Label>
        <Controller
          control={methods.control}
          name={"cafeDisclaimer"}
          defaultValue={selectedCafe?.disclaimer || ""}
          rules={{
            required: { value: true, message: "Required field." },
          }}
          ref={methods.register()}
          render={({ onChange, onBlur, value }) => (
            <MDEditor
              onBlur={onBlur}
              hideToolbar={false}
              preview="edit"
              textareaProps={{
                placeholder: "Please enter disclaimer here, required",
              }}
              commands={[linkCommand]}
              value={value}
              onChange={(value) => onChange(value && value.length > 250 ? value.substr(0, 250) : value || "")}
              className={methods.errors?.cafeDisclaimer?.message ? styles.textInputBoxError : styles.textInputBigBox}
              aria-label={"Cafe disclaimer, required"}
              aria-invalid={methods.errors.cafeDisclaimer ? "true" : "false"}
              data-testid={"cafeDisclaimer"}
              defaultTabEnable={true}
            />
          )}
        />
        {methods.errors?.cafeDisclaimer?.message && (
          <Text role={"alert"} className={styles.errorMessage}>
            {methods.errors?.cafeDisclaimer?.message}
          </Text>
        )}
      </Stack.Item>
    </Stack>
  );
};
